import React, { useCallback, useEffect, useState } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { getSessionId } from '../../utils/sessionId';
import menuStore from '../../utils/localForageInstance';
import { cachedCall } from '../../utils/dataCache';
import { toast } from 'react-toastify';
import '../../styles/DealPopupModal.css';
import '../../styles/HalfNHalfModal.css';

const emptyHalf = { productId: null, name: '', image: '', price: 0, defaultQtys: {}, addedQtys: {}, chilli: null, cheese: null, sauce: null, meat: null };

const HalfNHalfModal = ({ isOpen, onClose, orderType, editOrderId, onCartChange, onPendingAddHalf, onPendingSettle, onPendingFail }) => {
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [size, setSize] = useState(null);
  const [crust, setCrust] = useState(null);
  const [half1, setHalf1] = useState(emptyHalf);
  const [half2, setHalf2] = useState(emptyHalf);
  const [portionOpenFor, setPortionOpenFor] = useState(null); // 1 | 2 | null
  const [portionProducts, setPortionProducts] = useState([]);
  const [portionLoading, setPortionLoading] = useState(false);
  const [customiseFor, setCustomiseFor] = useState(null); // 1 | 2 | null
  const [recipes, setRecipes] = useState({});
  const [recipeLoading, setRecipeLoading] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const halves = { 1: [half1, setHalf1], 2: [half2, setHalf2] };

  useEffect(() => {
    if (!isOpen) return;
    setConfigLoading(true);
    setConfig(null);
    setSize(null);
    setCrust(null);
    setHalf1(emptyHalf);
    setHalf2(emptyHalf);
    setPortionOpenFor(null);
    setPortionProducts([]);
    setCustomiseFor(null);
    setRecipes({});

    cachedCall('hnh_config', () => callAPI(apiUrls.HALF_AND_HALF_CONFIG, {}, 'GET', null, {}, {}, false), { accept: (r) => r?.status === 200 && r.data?.status === 1 })
      .then(res => {
        const body = res.data || {};
        if (body.status === 1) {
          setConfig(body.data);
          if (body.data.sizes?.length > 0) setSize(body.data.sizes[0].id);
          if (body.data.crust?.length > 0) setCrust(body.data.crust[0].key);
        } else {
          setConfig(false);
        }
      })
      .catch(() => setConfig(false))
      .finally(() => setConfigLoading(false));
  }, [isOpen]);

  const fetchPortionProducts = useCallback(async (sizeId) => {
    setPortionLoading(true);
    try {
      const res = await cachedCall(`hnh_portion_${sizeId}`, () => callAPI(`${apiUrls.HALF_AND_HALF_PORTION_PRODUCTS}?size=${sizeId}`, {}, 'GET', null, {}, {}, false));
      const rows = (res.data?.data) || [];
      const enriched = await Promise.all(rows.map(async (row) => {
        const product = await menuStore.getItem(`product_${row.product_id}`);
        return {
          product_id: row.product_id,
          price: row.price,
          name: row.product_name || product?.product_name || `Item #${row.product_id}`,
          image: product?.product_img || '',
        };
      }));
      setPortionProducts(enriched);
    } catch {
      toast.error('Failed to load pizzas for this size');
      setPortionProducts([]);
    } finally {
      setPortionLoading(false);
    }
  }, []);

  const handleSizeSelect = (sizeId) => {
    setSize(sizeId);
    if (half1.productId || half2.productId) {
      setHalf1(emptyHalf);
      setHalf2(emptyHalf);
      setCustomiseFor(null);
    }
    setPortionProducts([]);
  };

  const openPortionPicker = (half) => {
    if (!size) return;
    setPortionOpenFor(half);
    fetchPortionProducts(size);
  };

  const recipeIsEmpty = (rec) =>
    !rec || (!rec.baseGroups?.length && !rec.included?.length && !rec.addons?.length && !rec.chilli?.length);

  const fetchRecipe = useCallback((productId) => {
    if (recipes[productId] !== undefined) return;
    setRecipeLoading(prev => ({ ...prev, [productId]: true }));
    cachedCall(`recipe_${productId}`, () => callAPI(`${apiUrls.PRODUCT_RECIPE}?id=${productId}`, {}, 'GET', null, {}, {}, false))
      .then(res => setRecipes(prev => ({ ...prev, [productId]: res.data || false })))
      .catch(() => setRecipes(prev => ({ ...prev, [productId]: false })))
      .finally(() => setRecipeLoading(prev => ({ ...prev, [productId]: false })));
  }, [recipes]);

  const pickProduct = (half, product) => {
    const [, setHalf] = halves[half];
    setHalf({ ...emptyHalf, productId: product.product_id, name: product.name, image: product.image, price: product.price });
    setPortionOpenFor(null);
    fetchRecipe(product.product_id);
  };

  const bothHalvesPicked = !!(half1.productId && half2.productId);

  const updateHalf = (half, patch) => {
    const [, setHalf] = halves[half];
    setHalf(prev => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  };

  const crustPrice = config && config.crust ? (config.crust.find(c => c.key === crust)?.price || 0) : 0;
  const addonPrice = config ? (config.addon_price || 0) : 0;
  const livePriceTotal = Math.max(half1.price || 0, half2.price || 0) + addonPrice + crustPrice;

  const canSubmit = !!(size && half1.productId && half2.productId && !submitting);

  // A base group with nothing picked yet uses its first free option (same default the deal pop-up pre-selects).
  const baseDefault = (bg) => (bg.options?.find(o => !o.price || o.price === 0) || bg.options?.[0])?.id;
  const baseChoice = (half, bg) => (half[bg.key] !== undefined ? half[bg.key] : baseDefault(bg));

  const buildFormData = (half) => {
    const rec = recipes[half.productId];
    const includedIngs = (rec?.included || []).map(ing => ({ id: ing.id, qty: half.defaultQtys[ing.id] !== undefined ? half.defaultQtys[ing.id] : 1 }));
    const addedQtys = half.addedQtys || {};
    const formData = {
      default_ingredients_quantity: JSON.stringify(includedIngs),
    };
    if (Object.keys(addedQtys).length > 0) {
      formData.add_on_ingredients_quantity = JSON.stringify(Object.entries(addedQtys).map(([id, qty]) => ({ id: Number(id), qty })));
    }
    // Bases: grouped products key by bases_<group_id> and post one JSON array; legacy ones post base1/2/3.
    const baseGroups = rec?.baseGroups || [];
    if (baseGroups.some(bg => bg.key.indexOf('bases_') === 0)) {
      const basesSel = baseGroups
        .filter(bg => baseChoice(half, bg))
        .map(bg => ({ id: baseChoice(half, bg), qty: 1, group: bg.key.replace(/^bases_/, '') }));
      if (basesSel.length) formData.bases_ingredients_quantity = JSON.stringify(basesSel);
    } else {
      baseGroups.forEach(bg => {
        const choice = baseChoice(half, bg);
        if (choice) formData[`${bg.key}_ingredients`] = choice;
      });
    }
    if (half.chilli) formData.chilli = half.chilli;
    if (half.cheese) formData.cheese = half.cheese;
    if (half.sauce) formData.sauce = half.sauce;
    if (half.meat) formData.meat = half.meat;
    return formData;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const session_id = getSessionId();
      const phone_order = (orderType === 'takeaway' || orderType === 'phone') ? 1 : 0;
      const payload = {
        portion1ItemId: half1.productId,
        portion2ItemId: half2.productId,
        size,
        crust,
        session_id,
        phone_order,
        selected_customer_id: 1,
        formData1: buildFormData(half1),
        formData2: buildFormData(half2),
      };
      if (editOrderId) payload.edit_order_id = editOrderId;

      // The Half n Half shows in the cart and the sheet closes at once; the call carries on in the
      // background (with the instant cart off, or without a price to show, tempId is null and it waits as before).
      const crustOption = config?.crust?.find((c) => c.key === crust);
      const sizeName = config?.sizes?.find((sz) => sz.id === size)?.name || '';
      const tempId = onPendingAddHalf
        ? onPendingAddHalf({
            total_price: livePriceTotal,
            crust_label: crustOption?.label?.trim() || '',
            crust_price: crustPrice,
            halves: [half1, half2].map((h) => ({ name: h.name, size: sizeName, image: h.image })),
          })
        : null;
      if (tempId) onClose();
      let placeholderHandled = false;
      try {
        const res = await callAPI(apiUrls.HALF_AND_HALF_ADD_TO_CART, {}, 'POST', payload, {}, {}, false);
        if (res.data?.status === 1) {
          if (tempId && onPendingSettle) { onPendingSettle(tempId); placeholderHandled = true; }
          toast.success('Half n Half added to cart');
          onCartChange?.();
          if (!tempId) onClose();
        } else {
          if (tempId && onPendingFail) { onPendingFail(tempId); placeholderHandled = true; }
          toast.error(tempId ? `Half n Half was not added: ${res.data?.message || 'the server did not accept it'}` : (res.data?.message || 'Failed to add Half n Half'));
        }
      } catch {
        if (tempId && onPendingFail) { onPendingFail(tempId); placeholderHandled = true; }
        toast.error(tempId ? 'Half n Half was not added: could not reach the server' : 'Failed to add Half n Half');
      } finally {
        // whatever happened, a row that was shown for this add is never left behind
        if (tempId && !placeholderHandled && onPendingFail) onPendingFail(tempId);
      }
    } catch {
      // building the request failed before anything was sent
      toast.error('Failed to add Half n Half');
    } finally {
      setSubmitting(false);
    }
  };

  const renderHalfCircleGraphic = (half) => {
    const leftFilled = half === 1;
    return (
      <svg className="hnh-half-circle" viewBox="0 0 200 200" width="200" height="200">
        <path d="M100,10 A90,90 0 0,0 100,190 Z" fill={leftFilled ? '#e8e8e8' : 'none'} stroke="#666" strokeWidth="1.5" />
        <path d="M100,10 A90,90 0 0,1 100,190 Z" fill={leftFilled ? 'none' : '#e8e8e8'} stroke="#666" strokeWidth="1.5" />
        <line x1="100" y1="10" x2="100" y2="190" stroke="#666" strokeWidth="1.5" />
      </svg>
    );
  };

  const renderHalfSlot = (half) => {
    const [halfData] = halves[half];
    if (!halfData.productId) {
      return (
        <div className="hnh-half-slot hnh-half-empty">
          <div className="hnh-half-visual">{renderHalfCircleGraphic(half)}</div>
          <div className="hnh-half-title">Half {half}</div>
          <button className="hnh-select-btn" disabled={!size} onClick={() => openPortionPicker(half)}>
            Select {half === 1 ? '1st' : '2nd'} Half
          </button>
        </div>
      );
    }
    return (
      <div className="hnh-half-slot">
        <div className="hnh-half-title">Half {half}</div>
        <div className="hnh-half-picked">
          {halfData.image
            ? <img className="hnh-half-img" src={halfData.image} alt={halfData.name} />
            : <div className="hnh-half-img hnh-half-img-placeholder">🍕</div>
          }
          <div className="hnh-half-info">
            <div className="hnh-half-name">{halfData.name}</div>
            <div className="hnh-half-price">${Number(halfData.price).toFixed(2)}</div>
          </div>
        </div>
        <div className="hnh-half-actions">
          <button className="hnh-link-btn" onClick={() => openPortionPicker(half)}>Change</button>
          <button
            className="hnh-link-btn"
            disabled={!bothHalvesPicked}
            title={!bothHalvesPicked ? 'Select both halves first' : undefined}
            onClick={() => { fetchRecipe(halfData.productId); setCustomiseFor(half); }}
          >
            Customise
          </button>
        </div>
      </div>
    );
  };

  const renderPortionPicker = () => (
    <div className="cp-wrap open">
      <div className="cp-hd">
        <button className="cp-back-btn" onClick={() => setPortionOpenFor(null)}>
          <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
            <path d="M6 1L1 6l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
      </div>
      <div className="cp-scroll">
        <div className="cp-section">
          <div className="cp-sec-hd">
            <span className="cp-sec-title">Select Half {portionOpenFor} pizza</span>
          </div>
          {portionLoading ? (
            <div className="deal-loading-center"><div className="deal-spinner" /></div>
          ) : (
            <div className="prod-grid">
              {portionProducts.length === 0 ? (
                <div className="prod-empty">No pizzas available for this size.</div>
              ) : portionProducts.map(p => (
                <div key={p.product_id} className="prod-card" onClick={() => pickProduct(portionOpenFor, p)}>
                  {p.image
                    ? <img className="prod-img" src={p.image} alt={p.name} />
                    : <div className="prod-img-placeholder">🍕</div>
                  }
                  <div className="prod-body">
                    <div className="prod-name">{p.name}</div>
                    <div className="prod-price-tag">${Number(p.price).toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderCustomisePanel = () => {
    if (!customiseFor) return null;
    const [halfData] = halves[customiseFor];
    const rec = recipes[halfData.productId];
    const isLoadingRecipe = recipeLoading[halfData.productId];

    return (
      <div className="cp-wrap open">
        <div className="cp-hd">
          <button className="cp-back-btn" onClick={() => setCustomiseFor(null)}>
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
              <path d="M6 1L1 6l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
        </div>
        <div className="cp-hero">
          {halfData.image
            ? <img src={halfData.image} alt={halfData.name} />
            : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#ede5d8,#d8cfc3)' }} />
          }
          <div className="cp-hero-txt">
            <div className="cp-hero-name">Half {customiseFor} — {halfData.name}</div>
          </div>
        </div>
        <div className="cp-scroll">
          {isLoadingRecipe ? (
            <div className="deal-loading-center"><div className="deal-spinner" /></div>
          ) : (
            <div className="cp-acc-list">
              {rec?.chilli?.length > 0 && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '14px 0 0' }}>
                  <div className="cp-sec-hd" style={{ marginBottom: 10 }}>
                    <span className="cp-sec-title">Spice Level</span>
                  </div>
                  <div className="size-row" style={{ paddingTop: 0 }}>
                    {rec.chilli.map(c => (
                      <button key={c.value} className={`size-pill${halfData.chilli === c.value ? ' on' : ''}`} onClick={() => updateHalf(customiseFor, { chilli: c.value })}>
                        <span className="sn">{c.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(rec?.baseGroups || []).map(bg => (
                <div className="acc open" key={bg.key}>
                  <div className="acc-hd"><span className="acc-title">{bg.label}</span></div>
                  <div className="acc-body"><div className="acc-inner"><div className="acc-pad">
                    {bg.options.map(opt => (
                      <div key={opt.id} className="radio-row" onClick={() => updateHalf(customiseFor, { [bg.key]: opt.id })}>
                        <div className={`radio-mark${baseChoice(halfData, bg) === opt.id ? ' on' : ''}`} />
                        <span className="radio-label">{opt.name}</span>
                        {opt.price > 0 && <span className="radio-price">+${opt.price}</span>}
                      </div>
                    ))}
                  </div></div></div>
                </div>
              ))}

              {/* Grouped ("new product config") products get one section per group, otherwise one flat list. */}
              {(rec?.isGroupedConfig && rec.includedGroups?.length > 0
                ? rec.includedGroups
                : rec?.included?.length > 0 ? [{ key: 'included', label: rec.includedLabel || 'Ingredients', options: rec.included }] : []
              ).map(grp => (
                <div className="acc open" key={grp.key}>
                  <div className="acc-hd"><span className="acc-title">{grp.label}</span></div>
                  <div className="acc-body"><div className="acc-inner"><div className="acc-pad">
                    {grp.options.map(ing => {
                      const qty = halfData.defaultQtys[ing.id] !== undefined ? halfData.defaultQtys[ing.id] : 1;
                      return (
                        <div key={ing.id} className={`ing-row${qty === 0 ? ' removed' : ''}`}>
                          <span className="ing-label"><span className="ing-name">{ing.name}</span></span>
                          <div className="ing-stepper">
                            <button onClick={() => updateHalf(customiseFor, prev => ({ defaultQtys: { ...prev.defaultQtys, [ing.id]: Math.max(0, qty - 1) } }))} disabled={qty === 0}>−</button>
                            <span className="v">{qty}</span>
                            <button onClick={() => updateHalf(customiseFor, prev => ({ defaultQtys: { ...prev.defaultQtys, [ing.id]: qty + 1 } }))}>+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div></div></div>
                </div>
              ))}

              {(rec?.isGroupedConfig && rec.addonGroups?.length > 0
                ? rec.addonGroups
                : rec?.addons?.length > 0 ? [{ key: 'addons', label: rec.addonLabel || 'Add-ons', options: rec.addons }] : []
              ).map(grp => (
                <div className="acc open" key={grp.key}>
                  <div className="acc-hd"><span className="acc-title">{grp.label}</span></div>
                  <div className="acc-body"><div className="acc-inner"><div className="acc-pad">
                    {grp.options.map(ing => {
                      const qty = (halfData.addedQtys || {})[ing.id] || 0;
                      return (
                        <div key={ing.id} className="ing-row">
                          <span className="ing-label">
                            <span className="ing-name">{ing.name}</span>
                            {ing.price > 0 && <span className="ing-price"> +${ing.price}</span>}
                          </span>
                          <div className="ing-stepper">
                            <button onClick={() => updateHalf(customiseFor, prev => {
                              const next = { ...prev.addedQtys };
                              const newQty = Math.max(0, (next[ing.id] || 0) - 1);
                              if (newQty === 0) delete next[ing.id]; else next[ing.id] = newQty;
                              return { addedQtys: next };
                            })} disabled={qty === 0}>−</button>
                            <span className="v">{qty}</span>
                            <button onClick={() => updateHalf(customiseFor, prev => ({ addedQtys: { ...prev.addedQtys, [ing.id]: (prev.addedQtys[ing.id] || 0) + 1 } }))}>+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div></div></div>
                </div>
              ))}

              {['cheese', 'sauce', 'meat'].map(group => (
                config?.[group]?.length > 0 && (
                  <div className="acc open" key={group}>
                    <div className="acc-hd"><span className="acc-title" style={{ textTransform: 'capitalize' }}>{group}</span></div>
                    <div className="acc-body"><div className="acc-inner"><div className="acc-pad">
                      {config[group].map(opt => {
                        const isOn = halfData[group] === opt.key;
                        return (
                          <div key={opt.key} className="radio-row" onClick={() => updateHalf(customiseFor, { [group]: opt.key })}>
                            <div className={`radio-mark${isOn ? ' on' : ''}`} />
                            <span className="radio-label">{opt.label}</span>
                            {opt.price > 0 && <span className="radio-price">+${opt.price}</span>}
                          </div>
                        );
                      })}
                    </div></div></div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
        <div className="cp-done-bar">
          <button className="cp-done-btn" onClick={() => setCustomiseFor(null)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8l3.5 3.5 6.5-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Done — save customisation
          </button>
        </div>
      </div>
    );
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="halfNHalfModal" closeOnEscape={!submitting} closeOnBackdropClick={!submitting}>
      <div className="deal-modal-root">
        <div className="deal-drag-handle" />
        <div className="deal-sheet-hd">
          <div className="deal-sheet-hd-text">
            <div className="deal-sheet-name">Build Your Half n Half</div>
          </div>
          <button className="deal-sheet-close" onClick={onClose} disabled={submitting} aria-label="Close">✕</button>
        </div>
        <div className="sheet-div" />

        <div className="deal-sheet-body">
          {configLoading ? (
            <div className="deal-loading-center"><div className="deal-spinner" /></div>
          ) : !config ? (
            <div className="deal-loading-center" style={{ color: 'var(--muted)', fontSize: 13 }}>Half n Half is not available for this restaurant.</div>
          ) : (
            <>
              <div className="hnh-halves-row">
                {renderHalfSlot(1)}
                {renderHalfSlot(2)}
              </div>

              <div className="cp-section">
                <div className="cp-sec-hd"><span className="cp-sec-title">Size</span></div>
                <div className="size-row" style={{ paddingTop: 0 }}>
                  {config.sizes.map(s => (
                    <button key={s.id} className={`size-pill${size === s.id ? ' on' : ''}`} onClick={() => handleSizeSelect(s.id)}>
                      <span className="sn">{s.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {config.crust?.length > 0 && (
                <div className="cp-section">
                  <div className="cp-sec-hd"><span className="cp-sec-title">Crust</span></div>
                  <div className="size-row" style={{ paddingTop: 0 }}>
                    {config.crust.map(c => (
                      <button key={c.key} className={`size-pill${crust === c.key ? ' on' : ''}`} onClick={() => setCrust(c.key)}>
                        <span className="sn">{c.label}</span>
                        {c.price > 0 && <span className="sp up">+${c.price}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {portionOpenFor && renderPortionPicker()}
        {!portionOpenFor && customiseFor && renderCustomisePanel()}

        {config && !portionOpenFor && !customiseFor && (
          <div className="sheet-ft">
            <div className="hnh-live-price">
              {livePriceTotal > 0 ? `$${livePriceTotal.toFixed(2)}` : ''}
            </div>
            <button className={`ft-next${canSubmit ? ' ready' : ' off'}`} onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? <span className="ft-spinner-btn" /> : <span>Add to Cart</span>}
            </button>
          </div>
        )}
      </div>
    </CommonModal>
  );
};

export default HalfNHalfModal;
